/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { InteractionLogService } from '@core/lib/log';

export enum PerformanceStatus {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  TIMEOUT = 'TIMEOUT'
}

export enum PerformanceSource {
  VIEWER = 'Viewer',
  REPORT = 'Report',
  DA = 'DA'
}

export enum PerformanceAction {
  STARTED = 'started',
  ENDED = 'ended',
  CLEARED = 'cleared'
}

interface PerformanceData {
  source: string;
  id: string | number;
  startTime: Date;
  endTime?: Date;
  elapsedTimeInSeconds?: number;
  status?: PerformanceStatus;
}

export interface PerformanceUpdate {
  source: PerformanceSource;
  id: string | number;
  action: PerformanceAction;
  formattedData?: string | null;
}

// Global object to hold performance data with source & id as unique key
const performanceRegistry: Map<string, PerformanceData> = new Map();

/**
 * PerformanceMonitor service for monitoring performance of viewer and reporting operations
 * This service provides a hybrid API with both static and instance methods:
 * - Static methods for core monitoring operations (start, stop, clear, clearAll)
 * - Instance methods for configuration and subscriptions (enableMonitoring, disableMonitoring, getUpdates, getUpdatesForSource) meant only for use in PerformanceMonitorComponent.
 *
 * @example
 * // Monitor operations (static methods)
 * PerformanceMonitorService.start(PerformanceSource.VIEWER, 'id');
 * PerformanceMonitorService.stop(PerformanceSource.VIEWER, 'id', PerformanceStatus.SUCCESS);
 */
@Injectable({
  providedIn: 'root'
})
export class PerformanceMonitorService {
  private static readonly performanceUpdates$ = new BehaviorSubject<PerformanceUpdate | null>(null);
  private static isMonitoringEnabled = false;
  private static interactionLogService: InteractionLogService;

  constructor(interactionLogService: InteractionLogService) {
    // Store the InteractionLogService for static method access
    PerformanceMonitorService.interactionLogService = interactionLogService;
  }

  /**
   * Custom logging method with service prefix
   */
  private static log(...args: any[]): void {
    console.debug('[PerformanceMonitorService]', ...args);
  }

  /**
   * Generates a unique key for the performance data based on source and id
   */
  private static getKey(source: PerformanceSource, id: string | number): string {
    const key = `${source}-${id}`;
    PerformanceMonitorService.log(`getKey() called with source: ${source}, id: ${id}, generated key: ${key}`);
    return key;
  }

  /**
   * Gets formatted performance data for a specific monitor
   */
  private static getFormattedData(source: PerformanceSource, id: string | number): string | null {
    PerformanceMonitorService.log(`getFormattedData() called with source: ${source}, id: ${id}`);

    const key = PerformanceMonitorService.getKey(source, id);
    const performanceData = performanceRegistry.get(key);
    PerformanceMonitorService.log(
      `Retrieved performance data for key ${key}:`,
      performanceData ? 'found' : 'not found'
    );

    if (!performanceData) {
      PerformanceMonitorService.log(`No performance monitoring found for ${source} with ID: ${id}`);
      return null;
    }

    if (performanceData.elapsedTimeInSeconds === undefined) {
      PerformanceMonitorService.log(`Performance monitoring for ${source} with ID: ${id} has not been ended yet`);
      return null;
    }

    let result = `${id} : ${performanceData.elapsedTimeInSeconds}`;

    // Add status only for ERROR and TIMEOUT
    if (performanceData.status === PerformanceStatus.ERROR || performanceData.status === PerformanceStatus.TIMEOUT) {
      result += ` : ${performanceData.status}`;
    }

    PerformanceMonitorService.log(`Formatted data: ${result}`);
    return result;
  }

  /**
   * Static method to start performance monitoring
   */
  static start(source: PerformanceSource, id: string | number, startTime?: Date): void {
    if (!PerformanceMonitorService.isMonitoringEnabled) return;

    PerformanceMonitorService.log(
      `start() called with source: ${source}, id: ${id}, startTime: ${startTime || 'undefined'}`
    );

    const key = PerformanceMonitorService.getKey(source, id);
    const actualStartTime = startTime || new Date();
    PerformanceMonitorService.log(`Generated key: ${key}, actualStartTime: ${actualStartTime.toISOString()}`);

    // Check if entry already exists and warn before overriding
    if (performanceRegistry.has(key)) {
      PerformanceMonitorService.log(`Overriding existing entry for ${source} with ID: ${id}.`);
    }

    const performanceData: PerformanceData = {
      source,
      id,
      startTime: actualStartTime
    };

    performanceRegistry.set(key, performanceData);

    // Emit update
    const update = {
      source,
      id,
      action: PerformanceAction.STARTED
    };
    PerformanceMonitorService.performanceUpdates$.next(update);
  }

  /**
   * Static method to end performance monitoring
   */
  static stop(source: PerformanceSource, id: string | number, status?: PerformanceStatus): void {
    if (!PerformanceMonitorService.isMonitoringEnabled) return;

    PerformanceMonitorService.log(`end() called with source: ${source}, id: ${id}, status: ${status || 'undefined'}`);
    const key = PerformanceMonitorService.getKey(source, id);
    const performanceData = performanceRegistry.get(key);

    if (!performanceData) {
      PerformanceMonitorService.log(`No performance monitoring found for ${source} with ID: ${id}`);
      return;
    }

    const endTime = new Date();
    const elapsedTimeInSeconds = (endTime.getTime() - performanceData.startTime.getTime()) / 1000;

    performanceData.endTime = endTime;
    performanceData.elapsedTimeInSeconds = elapsedTimeInSeconds;
    performanceData.status = status;

    const formattedData = PerformanceMonitorService.getFormattedData(source, id);
    PerformanceMonitorService.log(`Performance monitoring ended for ${source}: ${formattedData}`);

    // Log the launch time
    PerformanceMonitorService.log(`Logging performance data to interaction log service`);
    PerformanceMonitorService.interactionLogService.performanceLog(
      source,
      'launch time',
      performanceData.elapsedTimeInSeconds
    );

    // Emit update
    const update = {
      source,
      id,
      action: PerformanceAction.ENDED,
      formattedData
    };
    PerformanceMonitorService.performanceUpdates$.next(update);
  }

  /**
   * Static method to clear performance monitoring
   */
  static clear(source: PerformanceSource, id?: string | number): void {
    if (!PerformanceMonitorService.isMonitoringEnabled) return;

    PerformanceMonitorService.log(
      `clear() called with source: ${source}, id: ${id ?? 'undefined (clear all for source)'}`
    );
    if (id !== undefined) {
      // Clear specific monitor by id
      const key = PerformanceMonitorService.getKey(source, id);
      const wasDeleted = performanceRegistry.delete(key);
      if (wasDeleted) {
        PerformanceMonitorService.log(`Cleared performance monitor for ${source} with ID: ${id}`);
        // Emit update for specific clear
        const update = {
          source,
          id,
          action: PerformanceAction.CLEARED
        };
        PerformanceMonitorService.performanceUpdates$.next(update);
      } else {
        PerformanceMonitorService.log(`No performance monitor found for ${source} with ID: ${id}`);
      }
    } else {
      // Clear all monitors for this source
      const keysToDelete: string[] = [];
      const clearedIds: (string | number)[] = [];

      performanceRegistry.forEach((data, key) => {
        if (data.source === source) {
          keysToDelete.push(key);
          clearedIds.push(data.id);
          PerformanceMonitorService.log(`Found entry to delete: key=${key}, id=${data.id}`);
        }
      });

      keysToDelete.forEach(key => performanceRegistry.delete(key));
      PerformanceMonitorService.log(`Cleared ${keysToDelete.length} performance monitors for ${source}`);

      // Emit update for bulk clear
      clearedIds.forEach(clearedId => {
        const update = {
          source,
          id: clearedId,
          action: PerformanceAction.CLEARED
        };
        PerformanceMonitorService.performanceUpdates$.next(update);
      });
    }
  }

  /**
   * Static method to clear all performance monitoring
   */
  static clearAll(): void {
    if (!PerformanceMonitorService.isMonitoringEnabled) return;

    PerformanceMonitorService.log('clearAll() called.');
    const clearedUpdates: PerformanceUpdate[] = [];

    // Collect all data before clearing to emit updates
    performanceRegistry.forEach(data => {
      PerformanceMonitorService.log(`Found entry to clear: source=${data.source}, id=${data.id}`);
      clearedUpdates.push({
        source: data.source as PerformanceSource,
        id: data.id,
        action: PerformanceAction.CLEARED
      });
    });

    // Clear the entire registry
    const clearedCount = performanceRegistry.size;
    performanceRegistry.clear();

    PerformanceMonitorService.log(`Cleared all ${clearedCount} performance monitors across all sources`);

    // Emit update for each cleared monitor
    clearedUpdates.forEach(update => {
      PerformanceMonitorService.performanceUpdates$.next(update);
    });
  }

  /**
   * Instance method to get updates for a specific performance source.
   * Subscribe to this Observable to receive real-time updates.
   *
   * @param source - The performance source to monitor
   * @returns Observable of performance updates for the specified source
   */
  getUpdatesForSource(source: PerformanceSource): Observable<PerformanceUpdate | null> {
    PerformanceMonitorService.log(`getUpdatesForSource() called with source: ${source}`);
    return PerformanceMonitorService.performanceUpdates$.pipe(
      filter((update: PerformanceUpdate | null) => update !== null && update.source === source)
    );
  }

  /**
   * Instance method to get all performance updates from all sources.
   * Subscribe to this Observable to receive real-time updates.
   *
   * @returns Observable of all performance updates
   */
  getUpdates(): Observable<PerformanceUpdate | null> {
    return PerformanceMonitorService.performanceUpdates$.asObservable();
  }

  /**
   * Instance method to enable monitoring
   */
  enableMonitoring(): void {
    PerformanceMonitorService.isMonitoringEnabled = true;
    PerformanceMonitorService.log('Performance monitoring has been enabled');
  }

  /**
   * Instance method to disable monitoring
   */
  disableMonitoring(): void {
    PerformanceMonitorService.isMonitoringEnabled = false;
    PerformanceMonitorService.log('Performance monitoring has been disabled');
  }

  /**
   * Static method to check if monitoring is enabled
   */
  static isEnabled(): boolean {
    return PerformanceMonitorService.isMonitoringEnabled;
  }
}
